/**
 * ActivityFeed — Real-time event list from useDashboardStore.realtimeEvents.
 *
 * Auto-scrolling, event type icons, timestamps. Each event shows:
 * type badge, message summary, channel, timestamp.
 */
import { useEffect, useRef } from "react";
import { Activity, Bot, AlertTriangle, BarChart3, Server, RefreshCw, HeartPulse } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { useDashboardStore } from "@/stores/useDashboardStore";
import type { DashboardEvent } from "@ajnt/shared";

const eventIcons: Record<DashboardEvent["type"], typeof Activity> = {
  activity: Activity,
  agent_status: Bot,
  approval_request: AlertTriangle,
  usage_update: BarChart3,
  sandbox_state_change: Server,
  sandbox_session_update: RefreshCw,
  sandbox_health: HeartPulse,
};

const eventBadgeVariants: Record<DashboardEvent["type"], "primary" | "success" | "warning" | "danger"> = {
  activity: "primary",
  agent_status: "success",
  approval_request: "warning",
  usage_update: "primary",
  sandbox_state_change: "warning",
  sandbox_session_update: "primary",
  sandbox_health: "success",
};

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function getEventSummary(event: DashboardEvent): string {
  switch (event.type) {
    case "activity":
      return event.payload.description;
    case "agent_status":
      return `Agent ${event.payload.agentId} is ${event.payload.status}`;
    case "approval_request":
      return `Approval needed: ${event.payload.tool}`;
    case "usage_update":
      return `${event.payload.eventType}: ${event.payload.count} units`;
    case "sandbox_state_change":
      return `Sandbox state changed`;
    case "sandbox_session_update":
      return `Sandbox session updated`;
    case "sandbox_health":
      return `Sandbox health check`;
  }
}

export function ActivityFeed() {
  const events = useDashboardStore((s) => s.realtimeEvents);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events]);

  return (
    <Card data-testid="activity-feed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Activity Feed
          {events.length > 0 && (
            <Badge variant="primary" className="ml-auto">
              {events.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <EmptyState
            title="No activity yet"
            description="Events will appear here as your agents work"
            className="py-8"
          />
        ) : (
          <div
            ref={scrollRef}
            className="flex max-h-80 flex-col gap-2 overflow-y-auto"
          >
            {events.map((event, i) => {
              const Icon = eventIcons[event.type];
              return (
                <div
                  key={`${event.timestamp}-${i}`}
                  className="flex items-start gap-3 rounded-lg border border-white/5 bg-surface px-3 py-2.5"
                >
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant={eventBadgeVariants[event.type]}>
                        {event.type.replace("_", " ")}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(event.timestamp)}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm text-foreground">
                      {getEventSummary(event)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
